import { createGlobalStyle } from 'styled-components';

export function hexToRgba(colorHex: string, opacity: number): string {
  const rgbaColor = colorHex.replace('#', '');
  const r = parseInt(rgbaColor.substring(0, 2), 16);
  const g = parseInt(rgbaColor.substring(2, 4), 16);
  const b = parseInt(rgbaColor.substring(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

function paletteToAlphaSet(palette: string): Record<ColorAlpha, string> {
  return Array.from({ length: 10 }, (_, index) => {
    const alpha = (index + 1) * 10;
    const opacity = alpha / 100;
    return {
      [alpha]: hexToRgba(palette, opacity),
    } as Record<ColorAlpha, string>;
  }).reduce((acc, curr) => ({ ...acc, ...curr }), {} as Record<ColorAlpha, string>);
}

function colorDictionaryToGlobalVariables(colorDictionary: Record<string, Record<string, string>>): string[] {
  return Object.keys(colorDictionary)
    .map((_key) => {
      const key = _key.split('_').join('-'); // gray_light -> gray-light
      return Object.keys(colorDictionary[key]).map((subKey) => {
        // --gray-light-25: string as output
        return `--${key}-${subKey}: ${colorDictionary[key][subKey as keyof typeof colorDictionary]};`;
      });
    })
    .flat();
}

function colorModeDictionaryToGlobalVariables(
  colorModeDictionary: Record<string, Record<string, string[]>>,
  mode: 'light' | 'dark'
): string[] {
  return Object.keys(colorModeDictionary)
    .map((_key) => {
      const key = _key.split('_').join('-'); // gray_light -> gray-light
      return Object.keys(colorModeDictionary[key]).map((subKey) => {
        // --gray-light-25: string as output
        const subKeyIndex = mode === 'light' ? 0 : 1;
        return `--${key}-${subKey}: ${
          colorModeDictionary[key][subKey as keyof typeof colorModeDictionary][subKeyIndex]
        };`;
      });
    })
    .flat();
}

// Color Primitives
// ------------------------------------------------------------------------------------------------
const base = {
  black: '#000000',
  white: '#FFFFFF',
  transparent: 'transparent',
};

const gray_light = {
  25: '#FEFEFE',
  50: '#FAFAFA',
  100: '#F5F5F5',
  200: '#E5E5E5',
  300: '#D4D4D4',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#404040',
  800: '#262626',
  900: '#171717',
  950: '#0A0A0A',
};

const gray_dark = {
  25: '#FAFAFA',
  50: '#F7F7F7',
  100: '#F0F0F1',
  200: '#ECECED',
  300: '#CECFD2',
  400: '#94979C',
  500: '#85888E',
  600: '#61656C',
  700: '#373A41',
  800: '#22262F',
  900: '#13161B',
  950: '#0C0E12',
};

const brand = {
  25: '#E5FAF7',
  50: '#CCF6EF',
  100: '#A6EFE2',
  200: '#80E7D6',
  300: '#40DCC1',
  400: '#1AD5B5',
  500: '#00D0AD',
  600: '#0DC2A4',
  700: '#18B59B',
  800: '#228375',
  900: '#163639',
  950: '#092B2E',
  bodi: '#141921',
  podcast: '#D84547',
  'mighty-red': '#FA4D50',
};

const error = {
  25: '#FFFBFA',
  50: '#FEF3F2',
  100: '#FEE4E2',
  200: '#FECDCA',
  300: '#FDA29B',
  400: '#F97066',
  500: '#F04438',
  600: '#D92D20',
  700: '#B42318',
  800: '#912018',
  900: '#7A271A',
  950: '#55160C',
};

const warning = {
  25: '#FFFCF5',
  50: '#FFFAEB',
  100: '#FEF0C7',
  200: '#FEDF89',
  300: '#FEC84B',
  400: '#FDB022',
  500: '#F79009',
  600: '#DC6803',
  700: '#B54708',
  800: '#93370D',
  900: '#7A2E0E',
  950: '#E5E5E5',
};

const success = {
  25: '#F6FEF9',
  50: '#ECFDF3',
  100: '#DCFAE6',
  200: '#ABEFC6',
  300: '#75E0A7',
  400: '#47CD89',
  500: '#17B26A',
  600: '#079455',
  700: '#067647',
  800: '#085D3A',
  900: '#074D31',
  950: '#053321',
};

const gray_blue = {
  25: '#FCFCFD',
  50: '#F8F9FC',
  100: '#EAECF5',
  200: '#D5D9EB',
  300: '#B3B8DB',
  400: '#717BBC',
  500: '#4E5BA6',
  600: '#3E4784',
  700: '#363F72',
  800: '#293056',
  900: '#101323',
  950: '#0D0F1C',
};

const gray_cool = {
  25: '#FCFCFD',
  50: '#F9F9FB',
  100: '#EFF1F5',
  200: '#DCDFEA',
  300: '#B9C0D4',
  400: '#7D89B0',
  500: '#5D6B98',
  600: '#4A5578',
  700: '#404968',
  800: '#30374F',
  900: '#111322',
  950: '#0E101B',
};

const gray_modern = {
  25: '#FCFCFD',
  50: '#F8FAFC',
  100: '#EEF2F6',
  200: '#E3E8EF',
  300: '#CDD5DF',
  400: '#9AA4B2',
  500: '#697586',
  600: '#4B5565',
  700: '#4B5565',
  800: '#202939',
  900: '#121926',
  950: '#0D121C',
};

const gray_neutral = {
  25: '#FCFCFD',
  50: '#F9FAFB',
  100: '#F3F4F6',
  200: '#E5E7EB',
  300: '#D2D6DB',
  400: '#9DA4AE',
  500: '#6C737F',
  600: '#4D5761',
  700: '#384250',
  800: '#1F2A37',
  900: '#111927',
  950: '#0D121C',
};

const gray_iron = {
  25: '#FCFCFC',
  50: '#FAFAFA',
  100: '#F4F4F5',
  200: '#E4E4E7',
  300: '#D1D1D6',
  400: '#A0A0AB',
  500: '#70707B',
  600: '#51525C',
  700: '#3F3F46',
  800: '#26272B',
  900: '#1A1A1E',
  950: '#131316',
};

const gray_true = {
  25: '#FCFCFC',
  50: '#F7F7F7',
  100: '#F5F5F5',
  200: '#E5E5E5',
  300: '#D6D6D6',
  400: '#A3A3A3',
  500: '#737373',
  600: '#525252',
  700: '#424242',
  800: '#292929',
  900: '#141414',
  950: '#0F0F0F',
};

const gray_warm = {
  25: '#FDFDFC',
  50: '#FAFAF9',
  100: '#F5F5F4',
  200: '#E7E5E4',
  300: '#D7D3D0',
  400: '#A9A29D',
  500: '#79716B',
  600: '#57534E',
  700: '#44403C',
  800: '#292524',
  900: '#1C1917',
  950: '#171412',
};

const moss = {
  25: '#FAFDF7',
  50: '#F5FBEE',
  100: '#E6F4D7',
  200: '#CEEAB0',
  300: '#ACDC79',
  400: '#86CB3C',
  500: '#669F2A',
  600: '#4F7A21',
  700: '#3F621A',
  800: '#335015',
  900: '#2B4212',
  950: '#1A2808',
};

const green_light = {
  25: '#FAFEF5',
  50: '#F3FEE7',
  100: '#E3FBCC',
  200: '#D0F8AB',
  300: '#A6EF67',
  400: '#85E13A',
  500: '#66C61C',
  600: '#4CA30D',
  700: '#3B7C0F',
  800: '#326212',
  900: '#2B5314',
  950: '#15290A',
};

const green = {
  25: '#F6FEF9',
  50: '#EDFCF2',
  100: '#D3F8DF',
  200: '#AAFOC4',
  300: '#73E2A3',
  400: '#3CBF7F',
  500: '#16B364',
  600: '#099250',
  700: '#087443',
  800: '#095C37',
  900: '#084C2E',
  950: '#052E1C',
};

const teal = {
  25: '#F6FEFC',
  50: '#F0FDF9',
  100: '#CCFBF1',
  200: '#99F6E0',
  300: '#5FE9D0',
  400: '#2ED3B7',
  500: '#15B79E',
  600: '#0E9384',
  700: '#107569',
  800: '#125D56',
  900: '#134E48',
  950: '#0A2926',
};

const cyan = {
  25: '#F5FEFF',
  50: '#ECFDFF',
  100: '#CFF9FE',
  200: '#A5F0FC',
  300: '#67E3F9',
  400: '#22CCEE',
  500: '#06AED4',
  600: '#088AB2',
  700: '#0E7090',
  800: '#155B75',
  900: '#164C63',
  950: '#0D2D3A',
};

const blue_light = {
  25: '#F5FBFF',
  50: '#F0F9FF',
  100: '#E0F2FE',
  200: '#B9E6FE',
  300: '#7CD4FD',
  400: '#36BFFA',
  500: '#0BA5EC',
  600: '#0086C9',
  700: '#026AA2',
  800: '#065986',
  900: '#0B4A6F',
  950: '#062C41',
};

const blue = {
  25: '#F5FAFF',
  50: '#EFF8FF',
  100: '#D1E9FF',
  200: '#B2DDFF',
  300: '#84CAFF',
  400: '#53B1FD',
  500: '#2E90FA',
  600: '#1570EF',
  700: '#175CD3',
  800: '#1849A9',
  900: '#194185',
  950: '#102A56',
};

const blue_dark = {
  25: '#F5F8FF',
  50: '#EFF4FF',
  100: '#D1E0FF',
  200: '#B2CCFF',
  300: '#84ADFF',
  400: '#528BFF',
  500: '#2970FF',
  600: '#155EEF',
  700: '#004EEB',
  800: '#0040C1',
  900: '#00359E',
  950: '#002266',
};

const indigo = {
  25: '#F5F8FF',
  50: '#EEF4FF',
  100: '#E0EAFF',
  200: '#C7D7FE',
  300: '#A4BCFD',
  400: '#8098F9',
  500: '#6172F3',
  600: '#444CE7',
  700: '#3538CD',
  800: '#2D31A6',
  900: '#2D3282',
  950: '#1F235B',
};

const violet = {
  25: '#FBFAFF',
  50: '#F5F3FF',
  100: '#ECE9FE',
  200: '#DDD6FE',
  300: '#C3B5FD',
  400: '#A48AFB',
  500: '#875BF7',
  600: '#7839EE',
  700: '#6927DA',
  800: '#5720B7',
  900: '#491C96',
  950: '#2E125E',
};

const purple = {
  25: '#FAFAFF',
  50: '#F4F3FF',
  100: '#EBE9FE',
  200: '#D9D6FE',
  300: '#BDB4FE',
  400: '#9B8AFB',
  500: '#7A5AF8',
  600: '#6938EF',
  700: '#5925DC',
  800: '#4A1FB8',
  900: '#3E1C96',
  950: '#27115F',
};

const fuchsia = {
  25: '#FEFAFF',
  50: '#FDF4FF',
  100: '#FBE8FF',
  200: '#F6D0FE',
  300: '#EEAAFD',
  400: '#E478FA',
  500: '#D444F1',
  600: '#BA24D5',
  700: '#9F1AB1',
  800: '#821890',
  900: '#6F1877',
  950: '#47104C',
};

const pink = {
  25: '#FEF6FB',
  50: '#FDF2FA',
  100: '#FCE7F6',
  200: '#FCCEEE',
  300: '#FAA7E0',
  400: '#F670C7',
  500: '#EE46BC',
  600: '#DD2590',
  700: '#C11574',
  800: '#9E165F',
  900: '#851651',
  950: '#4E0D30',
};

const rose = {
  25: '#FFF5F6',
  50: '#FFF1F3',
  100: '#FFE4E8',
  200: '#FECDD6',
  300: '#FEA3B4',
  400: '#FD6F8E',
  500: '#F63D68',
  600: '#E31B54',
  700: '#C01048',
  800: '#A11043',
  900: '#89123E',
  950: '#510B24',
};

const orange_dark = {
  25: '#FFF9F5',
  50: '#FFF4ED',
  100: '#FFE6D5',
  200: '#FFD6AE',
  300: '#FF9C66',
  400: '#FF692E',
  500: '#FF4405',
  600: '#E62E05',
  700: '#BC1B06',
  800: '#97180C',
  900: '#771A0D',
  950: '#57130A',
};

const orange = {
  25: '#FEF6EE',
  50: '#FEF6EE',
  100: '#FDEAD7',
  200: '#F9DBAF',
  300: '#F7B27A',
  400: '#F38744',
  500: '#EF6820',
  600: '#E04F16',
  700: '#B93815',
  800: '#932F19',
  900: '#772917',
  950: '#511C10',
};

const yellow = {
  25: '#FEFDF0',
  50: '#FEFBE8',
  100: '#FEF7C3',
  200: '#FEEE95',
  300: '#FDE272',
  400: '#FAC515',
  500: '#EAAA08',
  600: '#CA8504',
  700: '#A15C07',
  800: '#854A0E',
  900: '#713B12',
  950: '#542C0D',
};

type ColorAlpha = 10 | 20 | 30 | 40 | 50 | 60 | 70 | 80 | 90 | 100;

const alpha_white_light: Record<ColorAlpha, string> = paletteToAlphaSet(base['white']);

const alpha_white_dark: Record<ColorAlpha, string> = paletteToAlphaSet(gray_dark[950]);

const alpha_black_light: Record<ColorAlpha, string> = paletteToAlphaSet(base['black']);

const alpha_black_dark: Record<ColorAlpha, string> = paletteToAlphaSet(base['white']);

// Color Primitives [Global Variables and Prop Keys]
// ------------------------------------------------------------------------------------------------
const colorScaleVariables = {
  'gray-light': gray_light,
  'gray-dark': gray_dark,
  brand,
  error,
  warning,
  success,
  'gray-blue': gray_blue,
  'gray-cool': gray_cool,
  'gray-modern': gray_modern,
  'gray-neutral': gray_neutral,
  'gray-iron': gray_iron,
  'gray-true': gray_true,
  'gray-warm': gray_warm,
  moss,
  'green-light': green_light,
  green,
  teal,
  cyan,
  'blue-light': blue_light,
  blue,
  'blue-dark': blue_dark,
  indigo,
  violet,
  purple,
  fuchsia,
  pink,
  rose,
  'orange-dark': orange_dark,
  orange,
  yellow,
};

const colorAlphaVariables = {
  'alpha-white-light': alpha_white_light,
  'alpha-white-dark': alpha_white_dark,
  'alpha-black-light': alpha_black_light,
  'alpha-black-dark': alpha_black_dark,
};

const colorPrimitiveGlobalVariables = colorDictionaryToGlobalVariables({
  base,
  ...colorScaleVariables,
  ...colorAlphaVariables,
});

export type ColorPrimitivesPropKeys =
  | `base-${keyof typeof base}`
  | `${keyof typeof colorScaleVariables}-${keyof (typeof colorScaleVariables)[keyof typeof colorScaleVariables]}`
  | `${keyof typeof colorAlphaVariables}-${keyof (typeof colorAlphaVariables)[keyof typeof colorAlphaVariables]}`;

export const ColorPrimitiveGlobalVariables = createGlobalStyle`
  :root {
    ${colorPrimitiveGlobalVariables}
  }
`;

// Gradients
// ------------------------------------------------------------------------------------------------
const gradient_gray = {
  '600-500-90deg': `linear-gradient(90deg, ${gray_light[600]} 0%, ${gray_light[500]} 100%)`,
  '700-600-45deg': `linear-gradient(45deg, ${gray_light[700]} 0%, ${gray_light[600]} 100%)`,
  '800-600-45deg': `linear-gradient(45deg, ${gray_light[800]} 0%, ${gray_light[600]} 100%)`,
  '800-600-90deg': `linear-gradient(90deg, ${gray_light[800]} 0%, ${gray_light[600]} 100%)`,
  '800-700-26deg': `linear-gradient(26deg, ${gray_light[800]} 0%, ${gray_light[700]} 100%)`,
  '900-600-45deg': `linear-gradient(45deg, ${gray_light[900]} 0%, ${gray_light[600]} 100%)`,
  '900-700-45deg': `linear-gradient(45deg, ${gray_light[900]} 0%, ${gray_light[700]} 100%)`,
  '50-white-180deg': `linear-gradient(180deg, ${gray_light[50]} 0%, ${base['white']} 100%)`,
  '100-white-180deg': `linear-gradient(180deg, ${gray_light[100]} 0%, ${base['white']} 100%)`,
  '100-25-180deg': `linear-gradient(180deg, ${gray_light[100]} 0%, ${gray_light[25]} 100%)`,
  '100-50-180deg': `linear-gradient(180deg, ${gray_light[100]} 0%, ${gray_light[50]} 100%)`,
  '200-25-180deg': `linear-gradient(180deg, ${gray_light[200]} 0%, ${gray_light[25]} 100%)`,
  '200-50-180deg': `linear-gradient(180deg, ${gray_light[200]} 0%, ${gray_light[50]} 100%)`,
  '200-100-180deg': `linear-gradient(180deg, ${gray_light[200]} 0%, ${gray_light[100]} 100%)`,
};

const gradient_brand = {
  '600-500-90deg': `linear-gradient(90deg, ${brand[600]} 0%, ${brand[500]} 100%)`,
  '700-600-45deg': `linear-gradient(45deg, ${brand[700]} 0%, ${brand[600]} 100%)`,
  '800-600-45deg': `linear-gradient(45deg, ${brand[800]} 0%, ${brand[600]} 100%)`,
  '800-600-90deg': `linear-gradient(90deg, ${brand[800]} 0%, ${brand[600]} 100%)`,
  '800-700-26deg': `linear-gradient(26deg, ${brand[800]} 0%, ${brand[700]} 100%)`,
  '900-600-45deg': `linear-gradient(45deg, ${brand[900]} 0%, ${brand[600]} 100%)`,
  '900-700-45deg': `linear-gradient(45deg, ${brand[900]} 0%, ${brand[700]} 100%)`,
};

const gradient_linear = {
  '01': 'linear-gradient(180deg, #00D0AD 0%, #BDF198 163.64%)',
};

// Gradients [Global Variables and Prop Keys]
// ------------------------------------------------------------------------------------------------
const gradientVariables = {
  'gradient-gray': gradient_gray,
  'gradient-brand': gradient_brand,
  'gradient-linear': gradient_linear,
};

const gradientGlobalVariables = colorDictionaryToGlobalVariables(gradientVariables);

export type GradientPropKeys =
  | `gradient-gray-${keyof typeof gradient_gray}`
  | `gradient-brand-${keyof typeof gradient_brand}`
  | `gradient-linear-${keyof typeof gradient_linear}`;

export const GradientGlobalVariables = createGlobalStyle`
  :root {
    ${gradientGlobalVariables}
  }
`;

// Color Modes - Text, Border, Foreground, Background, Focus
// ------------------------------------------------------------------------------------------------
const text = {
  primary: ['var(--brand-950)', 'var(--gray-dark-50)'],
  'primary-on-brand': ['var(--base-white)', 'var(--gray-dark-50)'],
  secondary: ['var(--gray-light-700)', 'var(--gray-dark-300)'],
  'secondary-hover': ['var(--gray-light-800)', 'var(--gray-dark-200)'],
  'secondary-on-brand': ['var(--brand-200)', 'var(--gray-dark-300)'],
  tertiary: ['var(--gray-light-600)', 'var(--gray-dark-400)'],
  'tertiary-hover': ['var(--gray-light-700)', 'var(--gray-dark-300)'],
  'tertiary-on-brand': ['var(--brand-200)', 'var(--gray-dark-400)'],
  quaternary: ['var(--gray-light-500)', 'var(--gray-dark-400)'],
  'quaternary-on-brand': ['var(--brand-300)', 'var(--gray-dark-400)'],
  white: ['var(--base-white)', 'var(--base-white)'],
  disabled: ['var(--gray-light-500)', 'var(--gray-dark-500)'],
  placeholder: ['var(--gray-light-500)', 'var(--gray-dark-500)'],
  'placeholder-subtle': ['var(--gray-light-300)', 'var(--gray-dark-700)'],
  'brand-primary': ['var(--brand-900)', 'var(--gray-dark-50)'],
  'brand-secondary': ['var(--brand-700)', 'var(--gray-dark-300)'],
  'brand-secondary-hover': ['var(--brand-800)', 'var(--gray-dark-200)'],
  'brand-tertiary': ['var(--brand-600)', 'var(--gray-dark-400)'],
  'brand-tertiary-alt': ['var(--brand-600)', 'var(--gray-dark-50)'],
  'brand-bodi': ['var(--brand-bodi)', 'var(--brand-bodi)'],
  'error-primary': ['var(--error-600)', 'var(--error-400)'],
  'error-primary-hover': ['var(--error-700)', 'var(--error-300)'],
  'warning-primary': ['var(--warning-600)', 'var(--warning-400)'],
  'success-primary': ['var(--success-600)', 'var(--success-400)'],
  'button-primary': ['var(--brand-950)', 'var(--gray-dark-700)'],
};

const border = {
  primary: ['var(--gray-light-300)', 'var(--gray-dark-700)'],
  secondary: ['var(--gray-light-200)', 'var(--gray-dark-800)'],
  'secondary-alt': ['var(--alpha-black-light-10)', 'var(--gray-dark-800)'],
  tertiary: ['var(--gray-light-100)', 'var(--gray-dark-800)'],
  disabled: ['var(--gray-light-300)', 'var(--gray-dark-700)'],
  'disabled-subtle': ['var(--gray-light-200)', 'var(--gray-dark-800)'],
  brand: ['var(--brand-500)', 'var(--brand-400)'],
  'brand-alt': ['var(--brand-600)', 'var(--gray-dark-700)'],
  error: ['var(--error-500)', 'var(--error-400)'],
  'error-subtle': ['var(--error-300)', 'var(--error-500)'],
};

const fg = {
  primary: ['var(--gray-light-900)', 'var(--base-white)'],
  secondary: ['var(--gray-light-700)', 'var(--gray-dark-300)'],
  'secondary-hover': ['var(--gray-light-800)', 'var(--gray-dark-200)'],
  tertiary: ['var(--gray-light-600)', 'var(--gray-dark-400)'],
  'tertiary-hover': ['var(--gray-light-700)', 'var(--gray-dark-300)'],
  quaternary: ['var(--gray-light-400)', 'var(--gray-dark-600)'],
  'quaternary-hover': ['var(--gray-light-500)', 'var(--gray-dark-500)'],
  white: ['var(--base-white)', 'var(--base-white)'],
  disabled: ['var(--gray-light-400)', 'var(--gray-dark-500)'],
  'disabled-subtle': ['var(--gray-light-300)', 'var(--gray-dark-600)'],
  'brand-primary': ['var(--brand-600)', 'var(--brand-500)'],
  'brand-primary-alt': ['var(--brand-600)', 'var(--gray-dark-300)'],
  'brand-secondary': ['var(--brand-500)', 'var(--brand-500)'],
  'brand-secondary-alt': ['var(--brand-500)', 'var(--gray-dark-600)'],
  'brand-secondary-hover': ['var(--brand-600)', 'var(--gray-dark-500)'],
  'error-primary': ['var(--error-600)', 'var(--error-500)'],
  'error-secondary': ['var(--error-500)', 'var(--error-400)'],
  'warning-primary': ['var(--warning-600)', 'var(--warning-500)'],
  'warning-secondary': ['var(--warning-500)', 'var(--warning-400)'],
  'success-primary': ['var(--success-600)', 'var(--success-500)'],
  'success-secondary': ['var(--success-500)', 'var(--success-400)'],
  'featured-icon-brand': ['var(--brand-600)', 'var(--brand-950)'],
};

const bg = {
  primary: ['var(--base-white)', 'var(--brand-950)'],
  'primary-alt': ['var(--base-white)', 'var(--brand-900)'],
  'primary-hover': ['var(--gray-light-50)', 'var(--gray-dark-800)'],
  'primary-solid': ['var(--brand-950)', 'var(--brand-900)'],
  secondary: ['var(--gray-light-50)', 'var(--brand-900)'],
  'secondary-alt': ['var(--gray-light-50)', 'var(--brand-950)'],
  'secondary-hover': ['var(--gray-light-100)', 'var(--gray-dark-800)'],
  'secondary-subtle': ['var(--gray-light-25)', 'var(--gray-dark-900)'],
  'secondary-solid': ['var(--gray-light-600)', 'var(--gray-dark-600)'],
  tertiary: ['var(--gray-light-100)', 'var(--gray-dark-800)'],
  quaternary: ['var(--gray-light-200)', 'var(--gray-dark-700)'],
  active: ['var(--gray-light-50)', 'var(--gray-dark-800)'],
  disabled: ['var(--gray-light-100)', 'var(--gray-dark-800)'],
  'disabled-subtle': ['var(--gray-light-50)', 'var(--gray-dark-900)'],
  overlay: ['var(--gray-light-950)', 'var(--gray-dark-800)'],
  'brand-primary': ['var(--brand-50)', 'var(--brand-500)'],
  'brand-primary-alt': ['var(--brand-50)', 'var(--brand-900)'],
  'brand-secondary': ['var(--brand-100)', 'var(--brand-600)'],
  'brand-solid': ['var(--brand-600)', 'var(--brand-600)'],
  'brand-solid-hover': ['var(--brand-700)', 'var(--brand-500)'],
  'brand-section': ['var(--brand-800)', 'var(--brand-900)'],
  'brand-section-subtle': ['var(--brand-700)', 'var(--brand-950)'],
  'brand-bodi': ['var(--brand-bodi)', 'var(--brand-bodi)'],
  'brand-podcast': ['var(--brand-podcast)', 'var(--brand-podcast)'],
  'brand-mighty-red': ['var(--brand-mighty-red)', 'var(--brand-mighty-red)'],
  'error-primary': ['var(--error-50)', 'var(--error-950)'],
  'error-secondary': ['var(--error-100)', 'var(--error-600)'],
  'error-solid': ['var(--error-600)', 'var(--error-600)'],
  'error-solid-hover': ['var(--error-700)', 'var(--error-500)'],
  'warning-primary': ['var(--warning-50)', 'var(--warning-950)'],
  'warning-secondary': ['var(--warning-100)', 'var(--warning-600)'],
  'warning-solid': ['var(--warning-600)', 'var(--warning-600)'],
  'success-primary': ['var(--success-50)', 'var(--success-950)'],
  'success-secondary': ['var(--success-100)', 'var(--success-600)'],
  'success-solid': ['var(--success-600)', 'var(--success-600)'],
};

const focus = {
  ring: ['var(--brand-500)', 'var(--brand-500)'],
  'ring-error': ['var(--error-500)', 'var(--error-500)'],
};

// Color Modes - Text, Border, Foreground, Background, Focus [Global Variables and Prop Keys]
// ------------------------------------------------------------------------------------------------

const colorModeVariables = {
  text,
  border,
  fg,
  bg,
  focus,
};

const colorModeLightGlobalVariables = colorModeDictionaryToGlobalVariables(colorModeVariables, 'light');
const colorModeDarkGlobalVariables = colorModeDictionaryToGlobalVariables(colorModeVariables, 'dark');

export type ColorModeTextPropKeys = `text-${keyof typeof text}`;
export type ColorModeBorderPropKeys = `border-${keyof typeof border}`;
export type ColorModeFgPropKeys = `fg-${keyof typeof fg}`;
export type ColorModeBgPropKeys = `bg-${keyof typeof bg}`;
export type ColorModeFocusPropKeys = `focus-${keyof typeof focus}`;

export type ColorModePropKeys =
  | ColorModeTextPropKeys
  | ColorModeBorderPropKeys
  | ColorModeFgPropKeys
  | ColorModeBgPropKeys
  | ColorModeFocusPropKeys
  | ColorPrimitivesPropKeys
  | GradientPropKeys;

export const ColorModeGlobalVariables = createGlobalStyle`
  :root {
    ${colorModeLightGlobalVariables.join('\n')}
  }

  ${'[data-color-mode="dark"]'} {
    ${colorModeDarkGlobalVariables.join('\n')}
  }

  ${'[data-color-mode="light"]'} {
    ${colorModeLightGlobalVariables.join('\n')}
  }

  .color-mode-dark {
    ${colorModeDarkGlobalVariables.join('\n')}
  }

  .color-mode-light {
    ${colorModeLightGlobalVariables.join('\n')}
  }
`;

// Shadows
// ------------------------------------------------------------------------------------------------

const shadow_color = {
  xs: [hexToRgba('#0A0D12', 0.05), 'transparent'],
  'sm-01': [hexToRgba('#0A0D12', 0.1), 'transparent'],
  'sm-02': [hexToRgba('#0A0D12', 0.1), 'transparent'],
  'md-01': [hexToRgba('#0A0D12', 0.1), 'transparent'],
  'md-02': [hexToRgba('#0A0D12', 0.06), 'transparent'],
  'lg-01': [hexToRgba('#0A0D12', 0.08), 'transparent'],
  'lg-02': [hexToRgba('#0A0D12', 0.03), 'transparent'],
  'lg-03': [hexToRgba('#0A0D12', 0.04), 'transparent'],
  'xl-01': [hexToRgba('#0A0D12', 0.08), 'transparent'],
  'xl-02': [hexToRgba('#0A0D12', 0.03), 'transparent'],
  'xl-03': [hexToRgba('#0A0D12', 0.04), 'transparent'],
  '2xl-01': [hexToRgba('#0A0D12', 0.18), 'transparent'],
  '2xl-02': [hexToRgba('#0A0D12', 0.04), 'transparent'],
  '3xl-01': [hexToRgba('#0A0D12', 0.14), 'transparent'],
  '3xl-02': [hexToRgba('#0A0D12', 0.04), 'transparent'],
  'skeumorphic-inner': [hexToRgba('#0A0D12', 0.05), hexToRgba('#0A0D12', 0.05)],
  'skeumorphic-inner-border': [hexToRgba('#0A0D12', 0.18), hexToRgba('#0A0D12', 0.18)],
};

const shadow = {
  none: 'none',
  xs: '0px 1px 2px 0px var(--shadow-color-xs)',
  'xs-skeuomorphic':
    '0px 0px 0px 1px var(--shadow-color-skeumorphic-inner-border) inset, 0px -2px 0px 0px var(--shadow-color-skeumorphic-inner) inset, 0px 1px 2px 0px var(--shadow-color-xs)',
  'focus-ring': '0px 0px 0px 2px var(--bg-primary), 0px 0px 0px 4px var(--focus-ring)',
  'focus-ring-xs-skeuomorphic': 'var(--shadow-xs-skeuomorphic), var(--shadow-focus-ring)',
  xl: '0px 20px 24px -4px var(--shadow-color-xl-01), 0px 8px 8px -4px var(--shadow-color-xl-02), 0px 3px 3px -1.5px var(--shadow-color-xl-03)',
  lg: '0px 2px 2px -1px var(--shadow-color-lg-03), 0px 4px 6px -2px var(--shadow-color-lg-02), 0px 12px 16px -4px var(--shadow-color-lg-01)',
};

// Shadows [Global Variables and Prop Keys]
// ------------------------------------------------------------------------------------------------
const shadowVariables = {
  'shadow-color': shadow_color,
};

const shadowColorLightGlobalVariables = colorModeDictionaryToGlobalVariables(shadowVariables, 'light');
const shadowColorDarkGlobalVariables = colorModeDictionaryToGlobalVariables(shadowVariables, 'dark');

const shadowGlobalVariables = colorDictionaryToGlobalVariables({ shadow });

export type ShadowPropKeys = `shadow-color-${keyof typeof shadow_color}` | `shadow-${keyof typeof shadow}`;

export type DataColorMode = 'light' | 'dark';

export const ShadowGlobalVariables = createGlobalStyle`
  :root {
    ${shadowColorLightGlobalVariables.join('\n')}
    ${shadowGlobalVariables.join('\n')}
  }

  ${'[data-color-mode="dark"]'} {
    ${shadowColorDarkGlobalVariables.join('\n')}
  }

  ${'[data-color-mode="light"]'} {
    ${shadowColorLightGlobalVariables.join('\n')}
  }

  .color-mode-dark {
    ${shadowColorDarkGlobalVariables.join('\n')}
  }

  .color-mode-light {
    ${shadowColorLightGlobalVariables.join('\n')}
  }
`;
